import { EditTwoTone } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Tag,
  theme,
} from "antd";
import React, { useEffect, useState } from "react";
import PracticesService, {
  ITimeZoneVatInitialValues,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { Utils } from "../../Utilities/Utils";
import { adminContext } from "../../Common/PageRoute";
import { getTimeZoneOptions } from "../../Common/TimeZoneUtils";
import { useUser } from "../../Context/UserContext";
import { AppDrawer } from "../../Common/Layout";

interface TimezoneAndVATProps {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
}

const TimezoneAndVAT = ({
  isEdit,
  setIsEdit,
  setLoading,
  practiceId,
}: TimezoneAndVATProps) => {
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const { token } = theme.useToken();
  const { openNotification } = React.useContext(adminContext);

  const [form] = Form.useForm();
  const [data, setData] = useState<ITimeZoneVatInitialValues>();
  const [saving, setSaving] = useState(false);

  const getTimeVatInformation = async () => {
    try {
      setLoading(true);
      const res =
        await PracticesService.getPracticeTimeVatEmailInformation(practiceId);
      if (res?.result && res?.status) {
        const fieldValues: ITimeZoneVatInitialValues = {
          timeZone: res.result.timeZone || "",
          vat: {
            enabled: !!res.result.vat?.enabled,
            rate: res.result.vat?.rate ?? 0,
          },
        };
        setData(fieldValues);
        form.setFieldsValue(fieldValues);
      } else {
        openNotification(
          "error",
          res?.message || "Error fetching timezone and VAT information",
        );
      }
    } catch {
      openNotification("error", "Error fetching timezone and VAT information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (practiceId) {
      getTimeVatInformation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const isEditing = isEdit.type === PracticeUpdateTypes.timeZoneVat;

  const handleOpenEdit = () => {
    if (data) {
      form.setFieldsValue({
        timeZone: data.timeZone || "",
        vat: {
          enabled: !!data.vat?.enabled,
          rate: data.vat?.rate ?? 0,
        },
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.timeZoneVat });
  };

  const handleCancel = () => {
    if (data) {
      form.setFieldsValue(data);
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };

  const saveTimeVatDetails = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setLoading(true);

      const params: ITimeZoneVatInitialValues = {
        timeZone: values.timeZone,
        vat: values.vat,
      };

      const res = await PracticesService.updatePracticeTimezoneVate(
        practiceId,
        params,
      );

      if (res?.result) {
        openNotification("success", "Timezone and VAT updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        await getTimeVatInformation();
      } else {
        openNotification(
          "error",
          res?.message || "Error updating timezone and VAT",
        );
      }
    } catch {
      openNotification("error", "Error updating timezone and VAT");
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        padding: "16px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: 12,
          marginBottom: 14,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: token.colorTextHeading,
            }}
          >
            Timezone & VAT Configuration
          </span>
          <div
            style={{
              fontSize: 11,
              color: token.colorTextSecondary,
              marginTop: 1,
            }}
          >
            Regional timezone setting and default VAT rate percentage for tax
            calculations.
          </div>
        </div>

        {!isViewOnly && (
          <Button
            size="small"
            type="text"
            shape="circle"
            icon={<EditTwoTone />}
            onClick={handleOpenEdit}
          />
        )}
      </div>

      {/* Unbordered Descriptions */}
      <Descriptions
        size="small"
        bordered={false}
        column={{ xs: 1, sm: 2, md: 3 }}
        labelStyle={{
          color: token.colorTextSecondary,
          fontWeight: 500,
          fontSize: 12,
        }}
        contentStyle={{
          color: token.colorText,
          fontWeight: 500,
          fontSize: 13,
        }}
      >
        <Descriptions.Item label="Time Zone">
          {data?.timeZone ? (
            <Tag color="geekblue" style={{ borderRadius: 4, margin: 0 }}>
              {data.timeZone}
            </Tag>
          ) : (
            "—"
          )}
        </Descriptions.Item>

        <Descriptions.Item label="VAT Status">
          {data?.vat?.enabled ? (
            <Tag color="green" style={{ borderRadius: 4, margin: 0 }}>
              Enabled
            </Tag>
          ) : (
            <Tag color="default" style={{ borderRadius: 4, margin: 0 }}>
              Disabled
            </Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="VAT Rate (%)">
          {data?.vat?.enabled ? (
            <span style={{ fontWeight: 600 }}>{data.vat.rate ?? 0}%</span>
          ) : (
            "—"
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Standard Edit Drawer */}
      <AppDrawer
        title="Edit Timezone & VAT"
        subtitle="Configure practice local time and statutory VAT rate"
        width={460}
        open={isEditing}
        onClose={handleCancel}
        onSave={saveTimeVatDetails}
        saveText="Save Changes"
        saveButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item
            label="Time Zone"
            name="timeZone"
            rules={[{ required: true, message: "Please select timezone" }]}
          >
            <Select
              showSearch
              placeholder="Select Timezone"
              options={getTimeZoneOptions()}
            />
          </Form.Item>

          <Form.Item
            valuePropName="checked"
            name={["vat", "enabled"]}
            style={{ marginBottom: 16 }}
          >
            <Checkbox>Enable VAT</Checkbox>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, cur) =>
              prev?.vat?.enabled !== cur?.vat?.enabled
            }
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue(["vat", "enabled"]) ? (
                <Form.Item
                  name={["vat", "rate"]}
                  label="VAT Rate (%)"
                  rules={[
                    {
                      required: true,
                      message: "VAT rate is required when VAT is enabled",
                    },
                  ]}
                >
                  <Input
                    onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
                    placeholder="Rate %"
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </AppDrawer>
    </div>
  );
};

export default TimezoneAndVAT;
